import { ReactNode } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SUPPORT_EMAIL } from "@/config/contact";

interface LegalPageProps {
  title: string;
  updated: string;
  intro?: ReactNode;
  children: ReactNode;
}

const LEGAL_LINKS = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/content-policy", label: "Content Policy" },
  { to: "/community-guidelines", label: "Community Guidelines" },
  { to: "/dmca", label: "DMCA / Copyright" },
];

const LegalPage = ({ title, updated, intro, children }: LegalPageProps) => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container max-w-4xl py-10 md:py-16">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-4">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Legal</span>
        </nav>

        <header className="mb-8 pb-6 border-b border-border">
          <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-2">{title}</h1>
          <p className="text-sm text-muted-foreground">Last updated: {updated}</p>
          {intro && <div className="mt-4 text-base text-muted-foreground leading-relaxed">{intro}</div>}
        </header>

        <div className="grid md:grid-cols-[1fr_220px] gap-10">
          <article className="legal-prose">
            {children}

            <div className="mt-12 rounded-lg border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Need to reach us?</p>
              <p>
                Email <a className="text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                {" or use our "}
                <Link to="/contact" className="text-primary hover:underline">contact form</Link>
                {" — every request (legal, privacy, copyright, support) routes to the same team."}
              </p>
            </div>
          </article>

          <aside className="hidden md:block">
            <div className="sticky top-24 rounded-lg border border-border bg-card p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Legal</p>
              <ul className="space-y-2 text-sm">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-foreground hover:text-primary transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
